import React, { useMemo, useState } from "react";
import Table from "../../../components/TableV2";
import TitleButtons from "../../../components/TitleButtons";
import { generatePdfReport } from "../../../utils/pdf/generatePdfReport";
import numeroParaExtenso from "../../../utils/numeroPorExtenso";
import { useEstoqueDisponivel } from "./estoqueData";
import { useNavigate } from "react-router-dom";
import TableActions from "../../../components/TableActions";

const SuprimentosEstoque: React.FC = () => {
  const navigate = useNavigate();
  const { itens, loading } = useEstoqueDisponivel();

  const headers = [
    "Posição",
    "Código",
    "NCM",
    "Nome do Produto",
    "Custo Unitário",
    "Moeda",
    "Quantidade",
    "Classificação",
  ];

  const columnWidths = [
    "10px",
    "70px",
    "70px",
    "350px",
    "100px",
    "60px",
    "10px",
    "50px",
  ];

  const [selectedColumns, setSelectedColumns] = useState<boolean[]>(
    headers.map(() => true)
  );

  const enabledColumnIndexes = selectedColumns
    .map((checked, i) => (checked ? i : -1))
    .filter((i) => i !== -1);

  /** ------------------ formatação por moeda ------------------ */
  const formatCurrency = (
    valor: string | number | null | undefined,
    moeda?: string | null
  ): string => {
    if (!moeda) return "N/A";
    const n = typeof valor === "string" ? Number(valor) : valor ?? 0;
    if (!isFinite(n)) return "N/A";

    const symbol =
      moeda === "USD"
        ? "$"
        : moeda === "EUR"
        ? "€"
        : moeda === "BRL"
        ? "R$"
        : "";

    if (!symbol) return "N/A";

    return `${symbol} ${n.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const moedaFromItem = (p: any) =>
    p?.moeda && String(p.moeda).trim() !== "" ? String(p.moeda) : null;

  // linhas da tabela
  const rows = useMemo(
    () =>
      itens.map((p) => {
        const moeda = moedaFromItem(p);
        return [
          p.posicao ?? "",
          p.codigo ?? "",
          p.ncm ?? "",
          p.nomeProduto ?? "",
          formatCurrency(p.custoUnitario, moeda), // custo com símbolo da moeda ou N/A
          moeda ?? "N/A", // coluna Moeda
          String(p.quantidade ?? 0),
          p.classificacao ?? "",
        ];
      }),
    [itens]
  );

  const itensRelatorio = useMemo(
    () => itens.filter((p) => (p.quantidade ?? 0) > 0),
    [itens]
  );

  const rowsReport = useMemo(
    () =>
      itensRelatorio.map((p) => {
        const moeda = moedaFromItem(p);
        return [
          p.posicao ?? "",
          p.codigo ?? "",
          p.ncm ?? "",
          p.nomeProduto ?? "",
          formatCurrency(p.custoUnitario, moeda),
          moeda ?? "N/A",
          String(p.quantidade ?? 0),
          p.classificacao ?? "",
        ];
      }),
    [itensRelatorio]
  );

  // === NOVO: base do RELATÓRIO (só quantidade > 0) ===

  const totalProdutosRel = useMemo(
    () => itensRelatorio.reduce((acc, p) => acc + (p.quantidade ?? 0), 0),
    [itensRelatorio]
  );

  const valorTotalRel = useMemo(
    () =>
      itensRelatorio.reduce((acc, p) => {
        const custo = Number(p.custoUnitario ?? 0);
        const qtd = p.quantidade ?? 0;
        return acc + (isFinite(custo * qtd) ? custo * qtd : 0);
      }, 0),
    [itensRelatorio]
  );

  const codigoIdx = headers.indexOf("Código");

  return (
    <div>
      <TitleButtons fullBleedLine title={"Departamento Pessoal - Estoque"} />
      <TableActions
        buttons={[
          {
            label: "Gerar Relatório",
            textColor: "#1C1B1B",
            onClick: () =>
              generatePdfReport({
                reportTitle: "Relatório de Estoque - Departamento Pessoal",
                fileNamePrefix: "Departamento Pessoal-Estoque",
                subtitle: "Informações dos Produtos no Estoque",
                headers,
                rows: rowsReport,
                custoColIndex: 4,
                enabledColumns: enabledColumnIndexes,
                footerLines: [
                  `Total de Produtos no Estoque: ${totalProdutosRel}`,
                  `Valor Total em Estoque: R$ ${valorTotalRel.toLocaleString(
                    "pt-BR",
                    { minimumFractionDigits: 2 }
                  )} (${numeroParaExtenso(Math.floor(valorTotalRel))} reais)`,
                ],
              }),
            variant: "secondary",
            dropdownOptions: headers.map((h, i) => ({
              label: h,
              checked: selectedColumns[i],
            })),
            onOptionChange: (index, checked) => {
              const updated = [...selectedColumns];
              updated[index] = checked;
              setSelectedColumns(updated);
            },
          },
          {
            label: "Realizar Movimentação",
            textColor: "#1C1B1B",
            onClick: () =>
              navigate("/recursos-humanos/estoque/realizar-movimentacao"),
            variant: "primary",
          },
        ]}
      />
      {loading ? (
        <div style={{ padding: 16 }}>Carregando estoque…</div>
      ) : (
        <Table
          headers={headers}
          rows={rows}
          columnWidths={columnWidths}
          onDoubleClick={(_viewIndex, rowData) => {
            const codigo = String(rowData[codigoIdx] ?? "");
            if (codigo) {
              navigate(
                `/recursos-humanos/produtos/${encodeURIComponent(codigo)}`
              );
            }
          }}
          responsiveWidths={{
            1300: {
              1: "10px",
              2: "70px",
              3: "0px",
              4: "350px",
              5: "100px",
              6: "60px",
              7: "10px",
              8: "50px",
            },
          }}
        />
      )}
    </div>
  );
};

export default SuprimentosEstoque;
